import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Button, Spinner, Alert, Modal } from 'react-bootstrap';
import { FiUsers, FiRefreshCw, FiMail, FiPhone, FiBriefcase, FiPlus, FiTrash2, FiEye, FiEdit } from 'react-icons/fi';
import { getAdminUsers, deleteAdminUser } from '../../services/api';
import { handleApiError } from '../../utils/errorHandler';
import { useTranslation } from '../../hooks/useTranslation';
import 'bootstrap/dist/css/bootstrap.css';

const AdminUsers = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [users, setUsers] = useState([]);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteError, setDeleteError] = useState(null);
    const [userToDelete, setUserToDelete] = useState(null);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await getAdminUsers();
            // API returns { users: [...] }
            setUsers(data?.users || data || []);
        } catch (e) {
            setError(handleApiError(e, t('pages.adminUsers.errorLoading')));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const handleDeleteClick = (e, user) => {
        e.stopPropagation(); // Empêcher le clic sur la ligne
        setUserToDelete(user);
        setShowDeleteModal(true);
        setDeleteError(null);
    };

    const handleDeleteConfirm = async () => {
        if (!userToDelete) return;
        
        try {
            setDeleteLoading(true);
            setDeleteError(null);
            await deleteAdminUser(userToDelete.user_uuid);
            // Recharger la liste après suppression
            await fetchUsers();
            setShowDeleteModal(false);
            setUserToDelete(null);
        } catch (err) {
            setDeleteError(handleApiError(err, t('pages.adminUsers.errorDeleting')));
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleCloseDeleteModal = () => {
        setShowDeleteModal(false);
        setUserToDelete(null);
        setDeleteError(null);
    };

    return (
        <div className="dashboard-page">
            <Card>
                <Card.Body>
                    <div className="page-header">
                        <div>
                            <h2 className="page-title">
                                <FiUsers className="me-2" />
                                {t('pages.adminUsers.title')}
                            </h2>
                            <p className="text-muted mb-0">{t('pages.adminUsers.subtitle')}</p>
                        </div>
                        <div className="d-flex gap-2">
                            <Button 
                                variant="primary" 
                                onClick={() => navigate('/admin/users/create')}
                                disabled={loading}
                            >
                                <FiPlus className="me-1" /> {t('pages.adminUsers.createUser')}
                            </Button>
                            <Button 
                                variant="outline-primary" 
                                onClick={fetchUsers}
                                disabled={loading}
                            >
                                <FiRefreshCw className={loading ? 'spinning' : ''} /> {t('pages.adminUsers.refresh')}
                            </Button>
                        </div>
                    </div>

                    {error && (
                        <Alert variant="danger" className="mt-3">
                            {error}
                        </Alert>
                    )}

                    <Card className="mt-4">
                        <Card.Header>
                            <h5 className="mb-0">{t('pages.adminUsers.userList')}</h5>
                        </Card.Header>
                        <Card.Body>
                            {loading ? (
                                <div className="text-center py-5">
                                    <Spinner animation="border" variant="primary" />
                                    <p className="mt-3">{t('pages.adminUsers.loadingUsers')}</p>
                                </div>
                            ) : users.length === 0 ? (
                                <div className="text-center py-5">
                                    <FiUsers size={48} className="text-muted mb-3" />
                                    <p className="text-muted">{t('pages.adminUsers.noUsersFound')}</p>
                                </div>
                            ) : (
                                <div className="table-responsive table-scroll">
                                    <Table hover striped className="admin-table">
                                        <thead>
                                            <tr>
                                                <th>{t('pages.adminUsers.fullName')}</th>
                                                <th>{t('pages.adminUsers.email')}</th>
                                                <th>{t('pages.adminUsers.phone')}</th>
                                                <th>{t('pages.adminUsers.company')}</th>
                                                <th className="text-end">{t('pages.adminUsers.actions')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {users.map((user) => (
                                                <tr key={user.user_uuid}>
                                                    <td>
                                                        <strong>{user.first_name || ''} {user.last_name || ''}</strong>
                                                    </td>
                                                    <td>
                                                        <FiMail className="me-1" />
                                                        {user.email || 'N/A'}
                                                    </td>
                                                    <td>
                                                        {user.phone_number ? (
                                                            <>
                                                                <FiPhone className="me-1" />
                                                                {user.phone_number}
                                                            </>
                                                        ) : (
                                                            'N/A'
                                                        )}
                                                    </td>
                                                    <td>
                                                        {user.company_name ? (
                                                            <>
                                                                <FiBriefcase className="me-1" />
                                                                {user.company_name}
                                                            </>
                                                        ) : (
                                                            <span className="text-muted">—</span>
                                                        )}
                                                    </td>
                                                    <td className="text-end">
                                                        <Button
                                                            variant="outline-info"
                                                            size="sm"
                                                            className="me-2"
                                                            onClick={() => navigate(`/admin/users/${user.user_uuid}`)}
                                                            title={t('pages.adminUsers.viewDetails')}
                                                        >
                                                            <FiEye />
                                                        </Button>
                                                        <Button
                                                            variant="outline-primary"
                                                            size="sm"
                                                            className="me-2"
                                                            onClick={() => navigate(`/admin/users/edit/${user.user_uuid}`)}
                                                            title={t('pages.adminUsers.edit')}
                                                        >
                                                            <FiEdit />
                                                        </Button>
                                                        <Button
                                                            variant="outline-danger"
                                                            size="sm"
                                                            onClick={(e) => handleDeleteClick(e, user)}
                                                            title={t('pages.adminUsers.delete')}
                                                        >
                                                            <FiTrash2 />
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                            {!loading && users.length > 0 && (
                                <div className="mt-3 text-muted text-center">
                                    {t('pages.adminUsers.totalUsers', { count: users.length })}
                                </div>
                            )}
                        </Card.Body>
                    </Card>

                    {/* Modal de confirmation de suppression */}
                    <Modal show={showDeleteModal} onHide={handleCloseDeleteModal}>
                        <Modal.Header closeButton>
                            <Modal.Title>
                                <FiTrash2 className="me-2" />
                                {t('pages.adminUsers.confirmDelete')}
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {deleteError && (
                                <Alert variant="danger" className="mb-3">
                                    <strong>{t('common.error')} :</strong> {deleteError}
                                </Alert>
                            )}
                            {userToDelete && (
                                <>
                                    <p dangerouslySetInnerHTML={{ __html: t('pages.adminUsers.deleteMessage', { name: `${userToDelete.first_name} ${userToDelete.last_name}` }) }} />
                                    <p className="text-muted">
                                        <small>{t('pages.adminUsers.deleteWarning')}</small>
                                    </p>
                                    {userToDelete.email && (
                                        <div className="mt-3 p-2 bg-light rounded">
                                            <small>
                                                <strong>{t('pages.adminUsers.email')} :</strong> {userToDelete.email}
                                            </small>
                                        </div>
                                    )}
                                </>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button 
                                variant="secondary" 
                                onClick={handleCloseDeleteModal}
                                disabled={deleteLoading}
                            >
                                {t('common.cancel')}
                            </Button>
                            <Button 
                                variant="danger" 
                                onClick={handleDeleteConfirm}
                                disabled={deleteLoading}
                            >
                                {deleteLoading ? (
                                    <>
                                        <Spinner size="sm" className="me-2" animation="border" />
                                        {t('pages.adminUsers.deleting')}
                                    </>
                                ) : (
                                    <>
                                        <FiTrash2 className="me-2" />
                                        {t('pages.adminUsers.confirmDeleteButton')}
                                    </>
                                )}
                            </Button>
                        </Modal.Footer>
                    </Modal>
                </Card.Body>
            </Card>
        </div>
    );
};

export default AdminUsers;
